import { useLazyQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { SEARCH_QUERY } from '../../../graphql/resolvers/searchQueries/search.gql';
import { VOUCH_COPY } from '../vouch.copy';
import './VouchSearch.scss';

interface Author {
  PK: string;
  username?: string | null;
  displayName?: string | null;
}

export function VouchSearch({
  onPick,
}: {
  onPick: (username: string) => void;
}) {
  const [query, setQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [executeSearch, { data }] = useLazyQuery(SEARCH_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) return;
    debounceRef.current = setTimeout(() => {
      executeSearch({ variables: { query: q, limit: 8 } });
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, executeSearch]);

  const searched = query.trim().length >= 2;
  const authors: Author[] = searched
    ? ((data as { search?: { authors?: Author[] } })?.search?.authors ?? [])
    : [];

  return (
    <div className="vouch-search">
      <input
        type="text"
        className="vouch-search__input"
        placeholder={VOUCH_COPY.searchPlaceholder}
        aria-label={VOUCH_COPY.searchLabel}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {searched && authors.length === 0 && (
        <p className="vouch-search__empty">{VOUCH_COPY.searchEmpty}</p>
      )}
      {authors.length > 0 && (
        <ul className="vouch-search__results">
          {authors
            .filter((a) => a.username)
            .map((a) => (
              <li key={a.PK}>
                <button
                  type="button"
                  className="vouch-search__result"
                  onClick={() => onPick(a.username as string)}
                >
                  {a.displayName ? (
                    <span className="vouch-search__name">{a.displayName}</span>
                  ) : null}
                  <span className="vouch-search__handle">@{a.username}</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
